import { Link } from "react-router-dom";
import { Store, Utensils, MapPin, Clock, BadgeCheck } from "lucide-react";
import { VerifiedBadge } from "@/components/marketplace/shared";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Rating } from "@/components/marketplace/Reviews";

export const DAYS = [["mon", "Segunda"], ["tue", "Terça"], ["wed", "Quarta"], ["thu", "Quinta"], ["fri", "Sexta"], ["sat", "Sábado"], ["sun", "Domingo"]];

export function DirectoryVerifiedBadge({ verified, size = "sm", testid }) {
  if (!verified) return null;
  return <span title="Identidade da loja confirmada pela ANGOLAUK+. Não é uma recomendação nem garantia de qualidade." className="inline-flex"><VerifiedBadge verified size={size} testid={testid} /></span>;
}

export function OpenNow({ open, testid = "open-now" }) {
  if (open === null || open === undefined) return null;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full ${open ? "bg-green-50 text-green-700 border border-green-200" : "bg-zinc-100 text-zinc-500"}`}><Clock size={9} /> {open ? "Aberto agora" : "Fechado"}</span>;
}

export function BusinessCard({ b }) {
  const d = b.directory || {};
  return (
    <Link to={`/negocios/${b.id}`} data-testid={`business-card-${b.id}`} className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-zinc-200 card-hover">
      <div className="w-14 h-14 rounded-xl bg-black text-white flex items-center justify-center overflow-hidden shrink-0">
        {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : b.kind === "restaurant" ? <Utensils size={20} /> : <Store size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap"><span className="truncate">{b.shop_name}</span><DirectoryVerifiedBadge verified={b.seller_verified} testid={`biz-verified-${b.id}`} /></div>
        {d.tagline && <div className="text-xs text-zinc-600 truncate">{d.tagline}</div>}
        <div className="text-[11px] text-zinc-500 truncate">{d.business_categories?.slice(0, 2).map(c => c.name).join(" · ") || (b.kind === "restaurant" ? "Restaurante" : "Loja")}</div>
        <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="flex items-center gap-1"><MapPin size={10} /> {b.city}</span>
          <Rating avg={b.rating_avg} count={b.rating_count} testid={`biz-rating-${b.id}`} />
          <OpenNow open={d.open_now} testid={`biz-open-${b.id}`} />
        </div>
      </div>
      {b.claimable && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 shrink-0">Por reivindicar</span>}
      <FavoriteButton type="business" id={b.id} className="shrink-0 -mr-1" />
    </Link>
  );
}

export function OpeningHours({ hours, openNow, testid = "opening-hours" }) {
  if (!hours || !Object.values(hours).some(v => v?.length)) return null;
  const today = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-zinc-200 p-4 mb-4">
      <h2 className="font-display font-bold text-base mb-2 flex items-center gap-2"><Clock size={14} /> Horário <OpenNow open={openNow} /></h2>
      <div className="text-xs space-y-1">
        {DAYS.map(([d, n]) => <div key={d} className={`flex justify-between ${d === today ? "font-semibold" : "text-zinc-600"}`}><span>{n}</span><span>{hours[d]?.length ? hours[d].map(h => `${h.open}–${h.close}`).join(", ") : "Fechado"}</span></div>)}
      </div>
    </div>
  );
}

export function CategoryChips({ tree, value, onChange, testid = "category-chips" }) {
  const main = tree.find(c => c.slug === value || c.children.some(x => x.slug === value));
  return (
    <div data-testid={testid}>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none">
        <button onClick={() => onChange("")} className="chip whitespace-nowrap" data-active={!value} data-testid="dir-cat-all">Todas</button>
        {tree.map(c => <button key={c.slug} onClick={() => onChange(c.slug)} data-testid={`dir-cat-${c.slug}`} className="chip whitespace-nowrap" data-active={main?.slug === c.slug}>{c.name}</button>)}
      </div>
      {main?.children?.length > 0 && (
        <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none">
          {main.children.map(c => <button key={c.slug} onClick={() => onChange(value === c.slug ? main.slug : c.slug)} data-testid={`dir-subcat-${c.slug}`} className="chip whitespace-nowrap !text-[11px]" data-active={value === c.slug}>{c.name}</button>)}
        </div>
      )}
    </div>
  );
}

export function VerifiedNote() {
  return <p className="text-[10px] text-zinc-400 flex items-start gap-1 mt-2"><BadgeCheck size={11} className="shrink-0 mt-px" /> «Loja Verificada» confirma apenas a identidade do negócio junto da ANGOLAUK+ — não é recomendação nem garantia de qualidade.</p>;
}
